import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ViewChildren,
  QueryList,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  lastValueFrom,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterEvent,
  MultiSelectModule,
} from 'primeng/multiselect';

import { EntityRef, GlobalFilter } from '@core/models';
import { GlobalFilterRepository } from '@store/global-filter.repository';
import { GlobalPropertyRepository } from '@store/global-properties.repository';
import { Storage } from '@core/utils/storage';
import { NavigationEnd, Router } from '@angular/router';
import { BrandService, EnterpriseService, HotelService } from '@shared/services';
import { SkeletonModule } from 'primeng/skeleton';
import { reorderItems } from '@core/utils/reorder-items';

interface StoredGlobalFilter {
  visible: boolean;
  selectSingleHotel: boolean;
  filter: {
    enterprises: string[];
    brands: string[];
    hotels: string[];
  };
}

const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  ButtonModule,
  DialogModule,
  MultiSelectModule,
  SkeletonModule,
];

@Component({
  standalone: true,
  selector: 'cwa-global-filter',
  templateUrl: './global-filter.component.html',
  styleUrls: ['./global-filter.component.scss'],
  imports: [...MODULES],
  providers: [TitleCasePipe],
})
export class GlobalFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  private fb = inject(FormBuilder);
  private globalPropRepo = inject(GlobalPropertyRepository);
  private globalFilterRepo = inject(GlobalFilterRepository);
  router = inject(Router);
  private titleCase = inject(TitleCasePipe);
  private enterpriseService = inject(EnterpriseService);
  private brandService = inject(BrandService);
  private hotelService = inject(HotelService);

  // Subject to unsubscribe from observables
  private unsubscribe$: Subject<void> = new Subject<void>();
  @ViewChildren(MultiSelect) multiSelects!: QueryList<MultiSelect>;

  visible = false;
  selectSingleHotel!: boolean;

  filterForm: FormGroup<GlobalFilterForm> = this.createForm();

  enterprises$!: Observable<EntityRef[]>;
  brands$!: Observable<EntityRef[]>;
  hotels$!: Observable<EntityRef[]>;

  enterpriseFilter$: Subject<string> = new Subject<string>();
  brandFilter$: Subject<string> = new Subject<string>();
  hotelFilter$: Subject<string> = new Subject<string>();

  private brandSelection$ = new Subject<string[]>();
  private hotelSelection$ = new Subject<{ brands: string[]; enterprises: string[] }>();

  maxFilterIdCharecterLimit = 1548;
  uuidCharecterLength = 36;
  isEnterpriseLoading = false;
  isBrandLoading = false;
  isHotelLoading = false;
  selectedEnterpriseIds: string[] = [];
  selectedBrandIds: string[] = [];
  selectedHotelIds: string[] = [];

  constructor() {
    this.globalFilterRepo.restoreSelectedHotelInfo();
    this.globalFilterRepo.selectSingleHotel$
      .pipe(
        tap((limit) => {
          this.selectSingleHotel = limit;
        }),
        takeUntilDestroyed()
      )
      .subscribe();

    this.globalFilterRepo.filter$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        tap((filter) => {
          // ========== CHECKPOINT 6 ==========
          // console.log('🟣 CHECKPOINT 6: Component Received State Update', {
          //   filter,
          //   willSyncToForm: true,
          // });
          // ===================================
          if (filter) {
            this.filterForm.patchValue({
              enterprises: filter.enterprises,
              brands: filter.brands,
              hotels: filter.hotels,
            });

            if (filter?.hotels?.length > 0) {
              this.selectedHotelIds = filter.hotels;
              this.hotelOnBlur();
            }

            if (filter?.enterprises?.length > 0) {
              this.selectedEnterpriseIds = filter.enterprises;
              this.enterpriseOnBlur();
            }

            if (filter?.brands?.length > 0) {
              this.selectedBrandIds = filter.brands;
              this.brandOnBlur();
            }
          }
        }),
        mergeMap((filters) => {
          // if (filters?.enterprises?.length || filters?.brands?.length) {
          return this.loadBrandAndHotels(filters?.enterprises ?? [], filters?.brands ?? []);
          // }
          // return EMPTY;
        }),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe();

    this.enterprises$ = this.globalPropRepo.enterprises$.pipe(
      tap((enterprise) => {
        this.setDefaultEnterprises(enterprise);
      }),
      map((e) => e.map((i) => ({ ...i, name: this.titleCase.transform(i.name) })))
    );
    this.brands$ = this.globalPropRepo.brands$.pipe(
      map((e) => e.map((i) => ({ ...i, name: this.titleCase.transform(i.name) })))
    );

    // Inside the GlobalFilterComponent constructor
    this.hotels$ = this.globalPropRepo.hotels$.pipe(
      map((hotels) => {
        const formatted = hotels.map((hotel) => ({
          ...(hotel as EntityRef),
          name: this.formatHotelName(hotel),
        }));
        return reorderItems(formatted, this.selectedHotelIds);
      })
    );

    this.brandSelection$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap((enterpriseIds) => {
          this.globalPropRepo.setBrands([]);
          this.globalPropRepo.setHotels([]);
          this.filterForm.get('brands')?.reset([], { emitEvent: false });
          this.filterForm.get('hotels')?.reset([], { emitEvent: false });
          return this.globalPropRepo.fetchBrands(enterpriseIds);
        }),
        tap((brands) => this.globalPropRepo.setBrands(brands)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.hotelSelection$
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap(({ brands, enterprises }) => {
          this.globalPropRepo.setHotels([]);
          this.filterForm.get('hotels')?.reset([], { emitEvent: false });
          return this.globalPropRepo.fetchHotels(brands, enterprises);
        }),
        tap((hotels) => this.globalPropRepo.setHotels(hotels)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    // Load Enterprise and related Brands
    this.globalPropRepo.fetchEnterprises().pipe(takeUntilDestroyed()).subscribe();

    // region Filter Properties by Text

    // this.enterpriseFilter$
    //   .pipe(
    //     debounceTime(300),
    //     distinctUntilChanged(),
    //     switchMap((search) => this.globalPropRepo.fetchEnterprises(search)),
    //     takeUntilDestroyed()
    //   )
    //   .subscribe();

    // this.brandFilter$
    //   .pipe(
    //     debounceTime(300),
    //     distinctUntilChanged(),
    //     map((search) => {
    //       const enterprises = this.filterForm.value.enterprises ?? [];
    //       return { enterprises, search };
    //     }),
    //     switchMap(({ enterprises, search }) =>
    //       this.globalPropRepo.fetchBrands(enterprises, [], search)
    //     ),
    //     tap((brands) => this.globalPropRepo.setBrands(brands)),
    //     takeUntilDestroyed()
    //   )
    //   .subscribe();

    this.hotelFilter$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((search) => {
          const brands = this.filterForm.value.brands ?? [];
          const enterprises = this.filterForm.value.enterprises ?? [];
          return { brands, enterprises, search };
        }),
        switchMap(({ brands, enterprises, search }) =>
          this.globalPropRepo.fetchHotels(brands, enterprises, [], [], search)
        ),
        tap((hotels) => this.globalPropRepo.setHotels(hotels)),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  enterpriseOnBlur() {
    this.enterprises$ = this.globalPropRepo.enterprises$.pipe(
      map((enterprise) => reorderItems(enterprise, this.selectedEnterpriseIds))
    );
  }

  brandOnBlur() {
    this.brands$ = this.globalPropRepo.brands$.pipe(
      map((brand) => reorderItems(brand, this.selectedBrandIds))
    );
  }

  hotelOnBlur() {
    // This will be called when the multiselect loses focus
    this.hotels$ = this.globalPropRepo.hotels$.pipe(
      map((hotels) => {
        const formatted = hotels.map((hotel) => ({
          ...hotel,
          name: this.formatHotelName(hotel),
        }));
        return reorderItems(formatted, this.selectedHotelIds);
      })
    );
  }

  private formatHotelName(hotel: EntityRef): string {
    // Check if the 'code' property exists on the hotel object
    const hotelCode = hotel.code || '';
    // Combine the hotel code and formatted name
    return `${hotelCode}- ${hotel.name}`;
  }

  // endregion
  private async setDefaultEnterprises(enterprise: EntityRef[]): Promise<void> {
    // Note: role-based logic removed as new user model doesn't have role property
    // This logic may need to be updated based on new permission model
    // const storage = Storage.getItem<User>('authUser');
    // const roleName = storage?.role.name;
    try {
      if (enterprise && enterprise.length) {
        // Check if there's already a saved filter in localStorage
        const existingFilterData: StoredGlobalFilter | null = Storage.getItem('globalFilter');

        // Only set all enterprises as default if there's no existing filter with selected enterprises
        if (
          !existingFilterData?.filter?.enterprises ||
          existingFilterData.filter.enterprises.length === 0
        ) {
          const ids = enterprise.map((e) => e.id);
          await lastValueFrom(this.loadBrandAndHotels(ids));
          // Removed: Enterprise dropdown should always be enabled for selection
          // this.filterForm.get('enterprises')?.disable();
          const value = this.filterForm.value as GlobalFilter;
          value.enterprises = ids;
          this.globalFilterRepo.setFilter(value);
        }
        // If there's already a saved filter with enterprises, don't overwrite it
        // Just load brands and hotels for the already selected enterprises
        else if (existingFilterData.filter.enterprises.length > 0) {
          await lastValueFrom(
            this.loadBrandAndHotels(
              existingFilterData.filter.enterprises,
              existingFilterData.filter.brands || []
            )
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  ngOnInit(): void {
    // this.setFirstHotelOnPropertyReport();
    this.setFirstHotelWhenSingleSelection();
    this.enterpriseService.loading$.subscribe((isLoading) => {
      this.isEnterpriseLoading = isLoading;
    });

    this.brandService.loading$.subscribe((isLoading) => {
      this.isBrandLoading = isLoading;
    });

    this.hotelService.loading$.subscribe((isLoading) => {
      this.isHotelLoading = isLoading;
    });
  }

  ngAfterViewInit(): void {
    // this.setFirstHotelOnPropertyReport();
    this.setFirstHotelWhenSingleSelection();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(); // Complete all subscriptions
    this.unsubscribe$.complete();
  }

  private loadBrandAndHotels(enterpriseIds: string[], brandIds?: string[] | null) {
    const brands$ = this.globalPropRepo.fetchBrands(enterpriseIds);

    const hotels$ = this.globalPropRepo.fetchHotels(brandIds ?? [], enterpriseIds);

    return forkJoin([brands$, hotels$]).pipe(catchError(() => of([])));
  }

  private calcualteFilterIdCharecterLength(items: string[]) {
    if (items == null || items == undefined) {
      items = [];
    }

    return items.length * this.uuidCharecterLength;
  }

  private calculateEnterpriseLengthLimit() {
    const value = this.filterForm.value as GlobalFilter;
    const brandLength = this.calcualteFilterIdCharecterLength(value.brands);
    const hotelLength = this.calcualteFilterIdCharecterLength(value.hotels);
    const maxLength = Math.floor(
      (this.maxFilterIdCharecterLimit - brandLength - hotelLength) / this.uuidCharecterLength
    );

    return maxLength;
  }

  private calculateBrandLengthLimit() {
    const value = this.filterForm.value as GlobalFilter;
    const enterpriseLength = this.calcualteFilterIdCharecterLength(value.enterprises);
    const hotelLength = this.calcualteFilterIdCharecterLength(value.hotels);
    const maxLength = Math.floor(
      (this.maxFilterIdCharecterLimit - enterpriseLength - hotelLength) / this.uuidCharecterLength
    );

    return maxLength;
  }

  private calculateHotelLengthLimit() {
    const value = this.filterForm.value as GlobalFilter;
    const brandLength = this.calcualteFilterIdCharecterLength(value.brands);
    const enterpriseLength = this.calcualteFilterIdCharecterLength(value.enterprises);
    const maxLength = Math.floor(
      (this.maxFilterIdCharecterLimit - brandLength - enterpriseLength) / this.uuidCharecterLength
    );

    return maxLength;
  }

  visibleChange(visible: boolean): void {
    this.globalFilterRepo.setVisible(visible);
  }

  async selectEnterprise(event: MultiSelectChangeEvent): Promise<void> {
    this.selectedEnterpriseIds = event.value;
    // Commenting out the below code as it is written in the brand selection subject observable
    // this.globalPropRepo.setBrands([]);
    // this.globalPropRepo.setHotels([]);
    // this.filterForm.get('brands')?.reset([]);
    // this.filterForm.get('hotels')?.reset([]);

    // if (event.value && event.value.length) {
    //   await lastValueFrom(this.loadBrandAndHotels(event.value));
    // }

    // Trigger brand loading using Subject
    this.brandSelection$.next(event.value);

    const value = this.filterForm.value as GlobalFilter;
    value.enterprises = event.value;
    this.globalFilterRepo.setFilter(value);

    // Trigger hotel loading using Subject while selecting enterprise
    this.hotelSelection$.next({ enterprises: event.value, brands: [] });
  }

  async selectBrand(event: MultiSelectChangeEvent): Promise<void> {
    this.selectedBrandIds = event.value;

    // Trigger hotel loading using Subject
    const enterprises = this.filterForm.value.enterprises ?? [];
    this.hotelSelection$.next({ brands: event.value, enterprises });

    const value = this.filterForm.value as GlobalFilter;
    value.brands = event.value;

    this.globalFilterRepo.setFilter(value);
  }

  async selectHotel(event: MultiSelectChangeEvent): Promise<void> {
    // Handle Single Select
    if (this.selectSingleHotel) {
      // Check if itemValue exists and has an id property
      if (event.itemValue && typeof event.itemValue === 'object' && event.itemValue.id) {
        // Set the selected hotel ID for single select
        this.filterForm.get('hotels')?.setValue([event.itemValue.id]);

        // Store the hotel info in signal AND localStorage
        this.globalFilterRepo.setSelectedHotelInfo({
          id: event.itemValue.id,
          name: event.itemValue.name,
          code: event.itemValue.code,
        });

        const value = this.filterForm.value as GlobalFilter;
        this.globalFilterRepo.setFilter(value);
        return;
      } else {
        console.error('Invalid hotel selection - missing itemValue or id:', event.itemValue);
      }
    }
    // Handle Multi-Select
    else {
      const value = this.filterForm.value as GlobalFilter;
      this.selectedHotelIds = event.value;
      value.hotels = event.value;
      this.globalFilterRepo.setSelectedHotelInfo(null);
      this.globalFilterRepo.setFilter(value);
    }
  }

  filterEnterprise($event: MultiSelectFilterEvent) {
    this.enterpriseFilter$.next($event.filter);
  }

  filterBrand($event: MultiSelectFilterEvent) {
    this.brandFilter$.next($event.filter);
  }

  filterHotel($event: MultiSelectFilterEvent) {
    this.hotelFilter$.next($event.filter);
  }

  setFirstHotelOnPropertyReport(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd), // Type guard filter
        takeUntil(this.unsubscribe$) // Unsubscribe when component is destroyed
      )
      .subscribe((event: NavigationEnd) => {
        if (event.url === '/report/property' || event.url === '/reviews/dashboard') {
          this.hotels$.subscribe(() => {
            const globalFilter: StoredGlobalFilter | null = Storage.getItem('globalFilter');
            if (globalFilter && globalFilter.filter && globalFilter.filter.hotels) {
              const firstHotelId = globalFilter?.filter?.hotels[0] ?? null;
              if (firstHotelId) {
                this.filterForm.get('hotels')?.setValue([firstHotelId]);
                const value = this.filterForm.value as GlobalFilter;
                this.globalFilterRepo.setFilter(value);
              }
            }
          });
        }
      });
  }

  setFirstHotelWhenSingleSelection(): void {
    this.globalFilterRepo.selectSingleHotel$
      .pipe(
        filter((isSingleHotel) => isSingleHotel), // Only proceed if single hotel selection is enabled
        switchMap(() => this.hotels$), // Get the list of hotels
        takeUntil(this.unsubscribe$) // Unsubscribe when the component is destroyed
      )
      .subscribe((hotels) => {
        this.selectedHotelIds = [];
        const globalFilter: StoredGlobalFilter | null = Storage.getItem('globalFilter');
        if (globalFilter && globalFilter.filter && globalFilter.filter.hotels) {
          const firstHotelId = globalFilter.filter.hotels[0] ?? null;
          const selectedHotel = hotels.find((hotel) => hotel.id === firstHotelId);
          if (firstHotelId) {
            this.selectedHotelIds = [firstHotelId];
            // Only set hotel info if selectedHotel exists with valid properties, otherwise set null
            if (selectedHotel && selectedHotel.id && selectedHotel.name && selectedHotel.code) {
              this.globalFilterRepo.setSelectedHotelInfo({
                id: selectedHotel.id,
                name: selectedHotel.name,
                code: selectedHotel.code,
              });
            } else {
              this.globalFilterRepo.setSelectedHotelInfo(null);
            }
            this.filterForm.get('hotels')?.setValue([firstHotelId]); // Set the first hotel ID
            const value = this.filterForm.value as GlobalFilter;
            this.globalFilterRepo.setFilter(value); // Update the global filter
          }
        }
      });
  }

  private createForm(): FormGroup<GlobalFilterForm> {
    return this.fb.group(<GlobalFilterForm>{
      enterprises: this.fb.control([]),
      brands: this.fb.control([]),
      hotels: this.fb.control([]),
    });
  }

  // 4. HOST LISTENERS (scroll detection)
  // @HostListener('window:scroll', ['$event'])
  @HostListener('window:scroll')
  onWindowScroll() {
    this.hideAllDropdowns();
  }

  // 5. PRIVATE METHODS (put hideAllDropdowns here)
  private hideAllDropdowns() {
    // Method 1: Using ViewChildren reference
    if (this.multiSelects) {
      this.multiSelects.forEach((multiSelect) => {
        if (multiSelect.overlayVisible) {
          multiSelect.hide();
        }
      });
    }
  }
}

interface GlobalFilterForm {
  enterprises: FormControl<string[]>;
  brands: FormControl<string[]>;
  hotels: FormControl<string[]>;
}
